import type { CutListItem, FittingType, PipeSegment, Fitting } from "@/hooks/use-sketch-state";
import { Badge } from "@/components/ui/badge";
import { ClipboardCopy, Ruler, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { generateSketchPDF } from "./sketch-pdf";

const fittingLabels: Record<FittingType, string> = {
  elbow90: "90° Ell",
  elbow45: "45° Ell",
  tee: "Tee",
  reducer: "Reducer",
  coupling: "Coupling",
  valve: "Valve",
  cap: "Cap",
};

interface Props {
  cutList: CutListItem[];
  totalFootage: number;
  segmentCount: number;
  fittingCount: number;
  segments: PipeSegment[];
  fittings: Fitting[];
  sketchName?: string;
}

const CutList = ({ cutList, totalFootage, segmentCount, fittingCount, segments, fittings, sketchName }: Props) => {
  const copyToClipboard = () => {
    const lines = ["PIPE CUT LIST", "=".repeat(40)];
    for (const item of cutList) {
      lines.push(`\n${item.size} ${item.material}`);
      lines.push("-".repeat(30));
      item.segments.forEach((s, i) => lines.push(`  Run ${i + 1}: ${s.lengthFt.toFixed(1)} ft`));
      lines.push(`  Subtotal: ${item.totalFt.toFixed(1)} ft`);
      if (item.fittings.length > 0) {
        lines.push("  Fittings:");
        item.fittings.forEach(f => lines.push(`    ${fittingLabels[f.type]} × ${f.count}`));
      }
    }
    lines.push(`\n${"=".repeat(40)}`);
    lines.push(`TOTAL: ${totalFootage.toFixed(1)} ft | ${segmentCount} runs | ${fittingCount} fittings`);
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Cut list copied to clipboard");
  };

  if (cutList.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground text-sm">
        <Ruler className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>Draw pipe runs to generate a cut list</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-3">
      {/* Summary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Cut List</h3>
          <Badge variant="secondary" className="text-xs">{totalFootage.toFixed(1)} ft total</Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={copyToClipboard} className="h-7 text-xs gap-1">
            <ClipboardCopy className="w-3.5 h-3.5" />
            Copy
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              generateSketchPDF(segments, fittings, cutList, totalFootage, sketchName);
              toast.success("PDF downloaded");
            }}
            className="h-7 text-xs gap-1"
          >
            <FileDown className="w-3.5 h-3.5" />
            PDF
          </Button>
        </div>
      </div>

      {/* Items */}
      {cutList.map(item => (
        <div key={`${item.size}|${item.material}`} className="border border-border rounded-lg p-2.5 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">{item.size}</span>
            <Badge variant="outline" className="text-[10px]">{item.material}</Badge>
          </div>
          <div className="space-y-0.5">
            {item.segments.map((s, i) => (
              <div key={s.id} className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Run {i + 1}</span>
                <span className="font-mono">{s.lengthFt.toFixed(1)} ft</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between text-xs font-medium text-foreground border-t border-border pt-1">
            <span>Subtotal</span>
            <span className="font-mono">{item.totalFt.toFixed(1)} ft</span>
          </div>
          {item.fittings.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-0.5">
              {item.fittings.map(f => (
                <Badge key={f.type} variant="secondary" className="text-[10px]">
                  {fittingLabels[f.type]} ×{f.count}
                </Badge>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Totals */}
      <div className="border-t border-border pt-2 flex items-center justify-between text-sm font-semibold text-foreground">
        <span>Grand Total</span>
        <span className="font-mono">{totalFootage.toFixed(1)} ft</span>
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span>{segmentCount} pipe run{segmentCount !== 1 ? "s" : ""}</span>
        <span>{fittingCount} fitting{fittingCount !== 1 ? "s" : ""}</span>
      </div>
    </div>
  );
};

export default CutList;
